import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType, InteractionRequiredAuthError, type AccountInfo, type AuthenticationResult, type EventMessage } from '@azure/msal-browser';
import { apiRequest, loginRequest } from '../authConfig';
import { registerTokenGetter, setActiveAccountKey, apiCall, ApiError, invalidateApiCache } from '../utils/apiHelpers';

export interface LockdownConfig {
    enabled: boolean;
    disableCopy: boolean;
    disablePrint: boolean;
    disableContextMenu: boolean;
    disableSaveShortcut: boolean;
    disableDevToolsShortcut: boolean;
    watermark: boolean;
}

export interface UserInfo {
    objectId: string;
    email: string;
    displayName: string;
    role: 'admin' | 'reviewer';
    status: 'invited' | 'active' | 'suspended';
    activatedAt?: string;
    preferences?: {
        technicalLevel?: 'novice' | 'intermediate' | 'expert';
        responseLength?: 'concise' | 'standard' | 'thorough';
    };
    lockdown?: LockdownConfig | null;
}

export interface AuthContextValue {
    userInfo: UserInfo | null;
    tokenReady: boolean;
    isAuthenticated: boolean;
    authError: string | null;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    /** Re-fetch /me to refresh preferences/displayName after settings save. */
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
    return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const { instance } = useMsal();
    // Use the MSAL active account explicitly so multi-account sign-in works.
    // After a page refresh MSAL may have cached accounts but no active one
    // (depends on how the previous session ended) — promote the first cached
    // account to active in that case so the user doesn't appear signed out.
    const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(() => {
        const explicit = instance.getActiveAccount();
        if (explicit) return explicit;
        const cached = instance.getAllAccounts();
        if (cached.length > 0) {
            instance.setActiveAccount(cached[0]);
            return cached[0];
        }
        return null;
    });
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [tokenReady, setTokenReady] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    useEffect(() => {
        const cb = instance.addEventCallback((event: EventMessage) => {
            switch (event.eventType) {
                case EventType.LOGIN_SUCCESS:
                case EventType.ACQUIRE_TOKEN_SUCCESS: {
                    const result = event.payload as AuthenticationResult | undefined;
                    if (result?.account) {
                        instance.setActiveAccount(result.account);
                        setActiveAccount(result.account);
                    }
                    break;
                }
                case EventType.LOGOUT_SUCCESS:
                case EventType.ACCOUNT_REMOVED:
                    setActiveAccount(null);
                    break;
                case EventType.ACCOUNT_ADDED: {
                    const current = instance.getActiveAccount();
                    if (current) setActiveAccount(current);
                    break;
                }
                default:
                    break;
            }
        });
        return () => {
            if (cb) instance.removeEventCallback(cb);
        };
    }, [instance]);

    const acquireToken = useCallback(async (): Promise<string | null> => {
        const account = instance.getActiveAccount() ?? activeAccount;
        if (!account) return null;
        try {
            const result = await instance.acquireTokenSilent({ ...apiRequest, account });
            // Use the ID token, not the access token. With OIDC-only scopes
            // (openid/profile/email) MSAL fills `accessToken` with a token for
            // Microsoft Graph (`aud: 00000003-0000-0000-c000-000000000000`),
            // which our Function App Easy Auth correctly rejects. The ID token
            // has the right audience (the SPA app reg client ID) and is what
            // Easy Auth is configured to validate.
            return result.idToken || result.accessToken;
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                // Do NOT pass the cached `account`. Its username is External
                // ID's synthetic `<objectId>@bacrexternalauth.onmicrosoft.com`
                // UPN; MSAL would forward that as `login_hint` and External
                // ID would match the local shadow record and ask for a
                // password the federated (Google) user doesn't have. Same
                // reasoning as the 401-retry branch in the effect below.
                await instance.acquireTokenRedirect({ ...apiRequest, prompt: 'select_account' });
                return null;
            }
            throw err;
        }
    }, [activeAccount, instance]);

    useEffect(() => {
        registerTokenGetter(acquireToken);
    }, [acquireToken]);

    useEffect(() => {
        setActiveAccountKey(activeAccount?.homeAccountId ?? null);
    }, [activeAccount]);

    const accountKey = activeAccount?.homeAccountId ?? null;
    useEffect(() => {
        if (!accountKey) {
            setUserInfo(null);
            setTokenReady(false);
            setAuthError(null);
            invalidateApiCache();
            return;
        }
        let cancelled = false;
        setTokenReady(false);
        setAuthError(null);
        (async () => {
            try {
                const me = await apiCall<UserInfo>('/me');
                if (cancelled) return;
                setUserInfo(me);
                // Clear the 401-retry flag on success so a future expired
                // token gets a fresh retry attempt rather than going straight
                // to "auth keeps failing."
                sessionStorage.removeItem(`docreview.authRetry::${accountKey ?? ''}`);
            } catch (err) {
                if (cancelled) return;
                if (err instanceof ApiError) {
                    if (err.status === 401) {
                        // Token expired or rejected by Easy Auth — kick MSAL
                        // into a redirect to re-auth silently if the user's
                        // upstream session is still valid, or prompt if not.
                        // The page reload after the bounce will re-run this
                        // effect with a fresh token.
                        //
                        // Loop guard: if we already redirected once for this
                        // account and STILL got 401 on the next page load, the
                        // problem is structural (Easy Auth audience mismatch,
                        // clock skew, etc.) — surface a hard error instead of
                        // bouncing the user through redirects forever.
                        const RETRY_KEY = `docreview.authRetry::${accountKey ?? ''}`;
                        const alreadyRetried = sessionStorage.getItem(RETRY_KEY) === '1';
                        if (alreadyRetried) {
                            sessionStorage.removeItem(RETRY_KEY);
                            setAuthError(
                                'Authentication keeps failing. Sign out completely (incognito window helps) and try again. If this persists, contact your administrator — the app token may be misconfigured.'
                            );
                        } else {
                            sessionStorage.setItem(RETRY_KEY, '1');
                            // Do NOT pass the cached `account` here. Its
                            // `username` is External ID's synthetic
                            // `<objectId>@bacrexternalauth.onmicrosoft.com`
                            // UPN, which MSAL forwards as `login_hint` —
                            // External ID then matches the local shadow
                            // record and asks for a local-account password
                            // that federated (Google) users don't have.
                            // Explicit `prompt: 'select_account'` here
                            // (and on `loginRequest` for the
                            // `loginRedirect` fallback below) forces the
                            // IdP picker so the user can re-pick Google.
                            try {
                                await instance.acquireTokenRedirect({ ...apiRequest, prompt: 'select_account' });
                                return;
                            } catch (e) {
                                try {
                                    await instance.loginRedirect(loginRequest);
                                    return;
                                } catch {}
                            }
                            setAuthError('Authentication failed. Sign in again.');
                        }
                    } else if (err.status === 403) {
                        setAuthError('Your account is not authorized to access this app. Contact your administrator.');
                    } else {
                        setAuthError(err.message);
                    }
                } else {
                    setAuthError((err as Error).message);
                }
                setUserInfo(null);
            } finally {
                if (!cancelled) setTokenReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [accountKey]);

    const login = useCallback(async () => {
        await instance.loginRedirect(loginRequest);
    }, [instance]);

    const logout = useCallback(async () => {
        await instance.logoutRedirect();
    }, [instance]);

    const refresh = useCallback(async () => {
        try {
            const me = await apiCall<UserInfo>('/me', { skipCache: true });
            setUserInfo(me);
        } catch {
            // best-effort; existing state stays
        }
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            userInfo,
            tokenReady,
            refresh,
            isAuthenticated: !!activeAccount,
            authError,
            login,
            logout
        }),
        [userInfo, tokenReady, activeAccount, authError, login, logout, refresh]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
